/**
 * @file   src\Pages\CWOTextSettings.tsx
 * @brief  BankInformation page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  React,
  Container,
  Button,
  Row,
  Col,
  useOutletContext,
  useNavigate,
  useEffect,
  useState,
  useRef,
} from "../utils/thirdpartyComponents";
import CWSelect from "../components/CWSelect";
import CWInput from "../components/CWInput";
import CWSwitch from "../components/CWSwitch";
import { AppContextType } from "../interfaces/generalInterface";
import { textSettingsSchema } from "../validations/textSettingsSchema";
import * as Yup from "yup";
import { pageURLs } from "../utils/pageUrls";
import { MessageToaster } from "../utils/toastUtil";
import { SolutionType } from "../utils/enums";
import axios from "../config/axiosConfig";
import FullLoader from "../components/FullLoader";
import { TextSettingsApi } from "../utils/apiUrls";

const CWOTextSettings = () => {
  const [loading, setLoading] = useState(false);
  // Instantiate MessageToaster
  const toaster = new MessageToaster();
  const navigate = useNavigate();

  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  useEffect(() => {
    handleUpdate();
    getCompanyTextSettings();
  }, []);

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Text Settings",
      url: "",
      showBackButton: false,
      showMainMenu: true,
      activeLink: "",
    });
  };

  // Convert SolutionType enum to an array of options
  const paymentOptions = [
    // { label: 'Select the solution', value: 0 }, // First option with value 0
    ...Object.keys(SolutionType)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        label: SolutionType[key as keyof typeof SolutionType],
        value: Number(key),
      })),
  ];

  const refs = useRef<{
    [key: string]: HTMLInputElement | HTMLTextAreaElement | null;
  }>({});

  // Initialize states for owner formData
  const [formData, setFormData] = useState({
    isMessageActive: true,
    messageType: 1,
    personalAccessToken: "",
  });

  // State to handle errors
  const [errors, setErrors] = useState<any>({});

  // To get company details
  const getCompanyTextSettings = async () => {
    try {
      const { data } = await axios.get(
        TextSettingsApi.CARWASH_OWNER_GET_TEXT_SETTINGS
      );
      if (data?.error === 0) {
        const owner_textsettings = data.data;
        setFormData({
          personalAccessToken: owner_textsettings.MessageToken,
          isMessageActive:
            owner_textsettings.IsMessageActive === 1 ? true : false,
          messageType:
            owner_textsettings.MessageType === 0
              ? 1
              : owner_textsettings.MessageType,
        });
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Triggers whenever options is selected
  const handleSelectChange = (selectedOption: any, name: string) => {
    const value = selectedOption ? selectedOption.value : "";

    // To remove validation when user enters input
    const keys = Object.keys(errors);
    if (keys.includes(name)) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // On input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // To remove validation when user enters input
    const keys = Object.keys(errors);
    if (keys.includes(name)) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    const newFormData = {
      ...formData,
      [name]: value,
    };

    setFormData(newFormData);
  };

  const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      ["isMessageActive"]: e.target.checked,
    });
  };

  //To validate form on clicking next
  const validateForm = async (): Promise<boolean> => {
    try {
      setLoading(true); // Show loader
      console.log(formData);
      await textSettingsSchema.validate(formData, { abortEarly: false });

      const newformData = {
        isMessageActive: formData.isMessageActive === true ? 1 : 0,
        messageType: formData.messageType,
        personalAccessToken: formData.personalAccessToken,
      };

      const responseLocationBankUpdate = await axios.post(
        TextSettingsApi.CARWASH_OWNER_SAVE_TEXT_SETTINGS,
        newformData
      );

      if (responseLocationBankUpdate?.data?.error === 0) {
        toaster.toastSuccess(responseLocationBankUpdate?.data?.message);
        navigate(`/${pageURLs.dashboard}`);
      } else {
        toaster.toastError(responseLocationBankUpdate?.data?.message);
      }

      setErrors({});
      return true;
    } catch (error) {
      // If validation fails on submit, then errors are shown
      if (error instanceof Yup.ValidationError) {
        const newErrors: any = {};
        error.inner.forEach((err) => {
          newErrors[err.path as keyof typeof formData] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    } finally {
      setLoading(false); // Hide loader
    }
  };

  return (
    <div className="hardware-listing-container addnew-item-wrap">
      <Container>
        <div className="hardware-list-wrap">
          <div className="form-inner mx-auto">
            <div className="stripe-payment-note mb-4">
              <h3 className="f-bold">Sales Message Usage Instructions</h3>
              <h6 className="f-bold">Logging In</h6>
              <ul>
                <li>
                  1. Go to the URL: Sales Message Login :{" "}
                  <a href="https://app.salesmessage.com/" target="_blank">
                    https://app.salesmessage.com/
                  </a>
                </li>
                <li>
                  2. Select the sign up button on the bottom of the screen.
                </li>
                <li>3. Please create your own Sales Message Account</li>
              </ul>
              <h6 className="f-bold">Generating a Personal Access Token</h6>
              <ul>
                <li>1. Select "Profile" from the left-side panel.</li>
                <li>2. Choose "Settings" from the profile menu.</li>
                <li>3. Scroll down and select "Developer."</li>
                <li>
                  4. Under the Developer section, select "Personal Access
                  Tokens."
                </li>
                <li>
                  5. A new page titled "Personal Access Tokens" will appear.
                  Click the "Create Token" button at the top right corner.
                </li>
                <li>6. A pop-up titled "Create Personal Token" will appear.</li>
                <li>
                  7. Enter a name in the Name field and click "Create Personal
                  Token."
                </li>
                <li>
                  8. A pop-up with the newly created token will be displayed.
                  Click on the token to copy it.
                </li>
              </ul>
              <h6 className="f-bold">Adding the Token to the Carwash App</h6>
              <ul>
                <li>1. Log in to the Carwash App as a car wash owner.</li>
                <li>
                  2. Select "Text Settings" from the hamburger menu (top right).
                  The "Text Settings" page will be displayed.
                </li>
                <li>
                  3. Paste the copied token into the "Sales Message Personal
                  Access Token" field and click the Save button.
                </li>
              </ul>
              <h6 className="f-bold">
                Verifying a Newly Added User in Sales Message
              </h6>
              <ul>
                <li>1. Once saved, add a new user for verification.</li>
                <li>
                  2. Log in to Sales Message and go to "Contacts" in the
                  left-side menu.
                </li>
                <li>
                  3. The newly added user should be listed on the "All Contacts"
                  page.
                  <ul>
                    <li>
                      If the user is not listed, click the three dots at the top
                      right corner and select "Import History."
                    </li>
                    <li>
                      This will display the Import History page, where you can
                      check the status of the newly added contact.
                    </li>
                  </ul>
                </li>
              </ul>
              <h6 className="f-bold">Texting:</h6>
              <p>
                To send a broadcast message using Sales message, you first need
                to navigate to the Broadcasts section (in side menu) and create
                a new broadcast. You'll then name your broadcast, select the
                recipients (using filters), choose the sending number, and draft
                your message (SMS or Ringless Voicemail). Finally, you can
                schedule the message to send immediately or at a later time.
              </p>
              <p>Here's a more detailed breakdown of the process:</p>
              <h6 className="f-bold">1. Create a New Broadcast:</h6>
              <p>
                In your Sales message account, locate the "Outbound" section in
                left menu and click "New Broadcast".
              </p>
              <h6 className="f-bold">2. Name Your Broadcast:</h6>
              <p>
                Give your broadcast a descriptive name for easy identification
                and future reference.
              </p>
              <h6 className="f-bold">3. Make a filter:</h6>
              <p>
                Go to Contacts, which is found on the left column. Click the
                square that in the gray horizontal bar saying name, then hit
                “select all” that appears in blue, or however many people you
                want to group together. Then hit manage tags, type in a name for
                your tag in the add tags search or create bar, then you hit add.
              </p>
              <p>
                Then go to outbound for the new broadcast, click on the contacts
                button, then type in the search tag button the name of your tag,
                select it, then select contacts in your bottom right corner, and
                it shows you the number of people you are messaging. You can
                also search for the already made tag containing those on your
                list who are active and those who are inactive.
              </p>
              <h6 className="f-bold">4. Identify Recipients:</h6>
              <p>
                Filters: Use filters to select specific contacts based on
                criteria like name, email, phone number, created date, updated
                date, or tags. If you want to send by Subscription status, then
                Select Subscription status and select Contains any of and
                provide the Subscription Name and Apply. Then the contacts will
                be displayed in the Contacts Tab in the same screen.
              </p>
              <h6 className="f-bold">4. Choose Sending Number:</h6>
              <p>
                Select the phone number from which the message will be sent. You
                can choose from your inbox
              </p>
              <h6 className="f-bold">5. Draft Your Message:</h6>
              <p>SMS: Type your text message.</p>
              <p>Ringless Voicemail: Upload a Ringless Voicemail.</p>
              <h6 className="f-bold">6. Schedule or Send:</h6>
              <p>
                Send Immediately: Select "Send Now" to send the message right
                away. Send Later: Schedule the message to send at a specific
                date and time. Remember to activate the message after
                scheduling.
              </p>
            </div>
            <div className="fee-switch d-flex align-items-center">
              <h5 className="f-bold title m-0">
                Enable Text Messaging to customers
              </h5>
              <CWSwitch
                id="statusswitch"
                name="statusswitch"
                checked={formData.isMessageActive}
                onChange={handleSwitchChange}
              />
            </div>

            <Row>
              <Col md={12}>
                <CWSelect
                  id="messageType"
                  name="messageType"
                  options={paymentOptions}
                  label="Select the solution"
                  errorMessage={errors.messageType && errors.messageType}
                  dropdownGap={0}
                  dropdownPosition="auto"
                  values={paymentOptions.filter(
                    (option) => option.value === formData.messageType
                  )}
                  onChange={(selectedOptions) =>
                    handleSelectChange(selectedOptions[0], "messageType")
                  }
                  placeholder="Select the solution"
                  multi={false}
                  searchable={false}
                  isMandatory
                  disabled={false}
                />
              </Col>
            </Row>

            <CWInput
              id="personalAccessToken"
              name="personalAccessToken"
              type="text"
              label="Sales Message Personal Access Token"
              onChange={handleChange}
              value={formData.personalAccessToken}
              errorMessage={errors.personalAccessToken}
              ref={(el) => (refs.current.personalAccessToken = el)}
              autoComplete="off"
              isMandatory
              autoFocus
            />

            <div className="btn-sec d-flex justify-content-end">
              <Button
                title="Cancel"
                variant="primary"
                className="secondary-btn"
                onClick={() => navigate(`/${pageURLs.dashboard}`)}
              >
                Cancel
              </Button>
              <Button
                title="Save"
                variant="primary"
                className="primary-btn"
                type="button"
                onClick={validateForm}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Container>
      {loading && <FullLoader />}
    </div>
  );
};

export default CWOTextSettings;
